import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import { updateDocument } from 'modules/documents/redux/documentActions';
import { ensureEquityMarketSharePurchaseContractTemplate } from 'modules/trading/api/ensureEquityMarketSharePurchaseContractTemplate';

import { editEquityMarketSharePurchaseContractTemplateModalInitValue } from '../constants/editEquityMarketSharePurchaseContractTemplateModalInitValue';
import { EditEquityMarketSharePurchaseContractTemplateModalContextType } from '../types/EditEquityMarketSharePurchaseContractTemplateModalContextType';

export const EditEquityMarketSharePurchaseContractTemplateModalContext =
  createContext<EditEquityMarketSharePurchaseContractTemplateModalContextType>(
    editEquityMarketSharePurchaseContractTemplateModalInitValue
  );

const normalizeEditorModel = (editorModel: unknown): string => {
  if (editorModel == null) return '';
  if (typeof editorModel === 'string') return editorModel;
  return '';
};

export const EditEquityMarketSharePurchaseContractTemplateModalProvider: FC<{
  children: ReactNode;
  societyId: string;
  size?: string;
}> = ({ children, societyId, size = sizes.XL }) => {
  const { t } = useTranslate();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const [documentId, setDocumentId] = useState<string | null>(null);
  const [contractText, setContractText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleCloseModal = useCallback(() => {
    store.dispatch(setModal(null));
  }, []);

  const registerTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    textareaRef.current = el;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);
        const doc = await ensureEquityMarketSharePurchaseContractTemplate(
          societyId
        );
        if (cancelled || !doc?._id) {
          if (!cancelled && !doc?._id) setLoadError(true);
          return;
        }
        setDocumentId(doc._id);
        setContractText(normalizeEditorModel(doc.editorModel));
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (societyId) load();

    return () => {
      cancelled = true;
    };
  }, [societyId]);

  const insertVariable = useCallback((token: string) => {
    const el = textareaRef.current;
    setContractText((prev) => {
      if (!el) return `${prev}${token}`;
      const start = el.selectionStart ?? prev.length;
      const end = el.selectionEnd ?? prev.length;
      const before = prev.slice(0, start);
      const after = prev.slice(end);
      const next = `${before}${token}${after}`;
      requestAnimationFrame(() => {
        const pos = start + token.length;
        el.focus();
        el.setSelectionRange(pos, pos);
      });
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!documentId) {
      errorSwal(t('EquityMarketContractTemplateSaveError'));
      return;
    }
    try {
      setIsSaving(true);
      const updated = (await store.dispatch(
        updateDocument(
          {
            _id: documentId,
            editorModel: contractText,
          },
          false
        )
      )) as { _id?: string } | undefined;
      if (updated?._id) {
        successSwal(t('EquityMarketContractTemplateSaved'));
        handleCloseModal();
      } else {
        errorSwal(t('EquityMarketContractTemplateSaveError'));
      }
    } catch {
      errorSwal(t('EquityMarketContractTemplateSaveError'));
    } finally {
      setIsSaving(false);
    }
  }, [contractText, documentId, handleCloseModal, t]);

  const contextValue: EditEquityMarketSharePurchaseContractTemplateModalContextType =
    useMemo(
      () => ({
        data: {
          societyId,
          documentId,
          contractText,
          isLoading,
          isSaving,
          loadError,
          showPreview,
        },
        actions: {
          handleCloseModal,
          setContractText,
          insertVariable,
          setShowPreview,
          handleSave,
          registerTextareaRef,
        },
      }),
      [
        societyId,
        documentId,
        contractText,
        isLoading,
        isSaving,
        loadError,
        showPreview,
        handleCloseModal,
        insertVariable,
        handleSave,
        registerTextareaRef,
      ]
    );

  return (
    <EditEquityMarketSharePurchaseContractTemplateModalContext.Provider
      value={contextValue}
    >
      {children}
    </EditEquityMarketSharePurchaseContractTemplateModalContext.Provider>
  );
};

export const useEditEquityMarketSharePurchaseContractTemplateModal = () =>
  useContext(EditEquityMarketSharePurchaseContractTemplateModalContext);
